/**
 * RefundSlice
 * 
 * Main slice definition for refund state management.
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, RefundSliceState } from './state';
import { buildRefundExtraReducers } from './extraReducers';
import * as reducers from './reducers';

// Re-export selectors
export * from './selectors';

const refundSlice = createSlice({
  name: 'refund',
  initialState,
  reducers: {
    // Core reducers
    updateRefund: reducers.updateRefund,
    restoreFromBackup: reducers.restoreFromBackup,
    clearBackup: reducers.clearBackup,
    createNewRefund: reducers.createNewRefund,
    resetRefund: reducers.resetRefund,
    forceResetRefund: reducers.forceResetRefund,
    
    // Sub-entity reducers
    updateRefundDetails: reducers.updateRefundDetails,
    updateCheckInfo: reducers.updateCheckInfo,
    
    // Notes reducers
    addNote: reducers.addNote,
    updateNote: reducers.updateNote,
    removeNote: reducers.removeNote,
    
    // Approvals reducers
    addApproval: reducers.addApproval,
    updateApproval: reducers.updateApproval,
    
    // Edit tracking reducers
    markAsSaved: reducers.markAsSaved,
    discardChanges: reducers.discardChanges,
    setValidationError: reducers.setValidationError,
    clearValidationError: reducers.clearValidationError,
    clearAllValidationErrors: reducers.clearAllValidationErrors,
  },
  extraReducers: buildRefundExtraReducers,
});

// Export all actions with explicit naming to avoid conflicts
export const refundActions = refundSlice.actions;

// Export actions with descriptive names for imports
export const {
  updateRefund: updateRefundAction,
  restoreFromBackup: restoreFromBackupAction,
  clearBackup: clearBackupAction,
  createNewRefund: createNewRefundAction,
  resetRefund: resetRefundAction,
  forceResetRefund: forceResetRefundAction,
  updateRefundDetails: updateRefundDetailsAction,
  updateCheckInfo: updateCheckInfoAction,
  addNote: addNoteAction,
  updateNote: updateNoteAction,
  removeNote: removeNoteAction,
  addApproval: addApprovalAction,
  updateApproval: updateApprovalAction,
  markAsSaved: markAsSavedAction,
  discardChanges: discardChangesAction,
  setValidationError: setValidationErrorAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
} = refundSlice.actions;

// Default export for reducer
export default refundSlice.reducer;

// Type export
export type { RefundSliceState } from './state';
